import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Sun, CloudSun, Cloud, CloudFog, CloudRain, CloudSnow, CloudLightning, Droplets } from "lucide-react";

/** WMO weather code -> lucide icon + brand-consistent tint */
const iconFor = (code) => {
  if (code === 0) return { Icon: Sun, cls: "text-amber-500" };
  if (code <= 2) return { Icon: CloudSun, cls: "text-amber-500" };
  if (code === 3) return { Icon: Cloud, cls: "text-muted-foreground" };
  if (code <= 48) return { Icon: CloudFog, cls: "text-muted-foreground" };
  if (code <= 67) return { Icon: CloudRain, cls: "text-primary" };
  if (code <= 77) return { Icon: CloudSnow, cls: "text-sky-500" };
  if (code <= 86) return { Icon: CloudRain, cls: "text-primary" };
  return { Icon: CloudLightning, cls: "text-amber-600" };
};

// Module-level dedup: several job cards in the same city share ONE request.
const weatherPromises = new Map();
const fetchWeather = (city) => {
  if (!weatherPromises.has(city)) {
    weatherPromises.set(
      city,
      api.get(`/weather/today?city=${encodeURIComponent(city)}`).then(
        (res) => res.data,
        (err) => { weatherPromises.delete(city); throw err; }
      )
    );
  }
  return weatherPromises.get(city);
};

/**
 * Compact job-site weather chip.
 * `size="sm"` renders an extra-small version for job cards.
 * Units follow the app language (EN -> °F, PT -> °C).
 * Renders nothing while loading or on error (never blocks the layout).
 */
export const WeatherChip = ({ city, size = "md" }) => {
  const { lang } = useApp();
  const [w, setW] = useState(null);

  useEffect(() => {
    let alive = true;
    setW(null);
    if (!city) return undefined;
    fetchWeather(city)
      .then((data) => { if (alive) setW(data); })
      .catch(() => { if (alive) setW(null); });
    return () => { alive = false; };
  }, [city]);

  if (!w || w.temp_c == null) return null;

  const toF = (c) => Math.round((c * 9) / 5 + 32);
  const useF = lang === "en";
  const fmt = (c) => (useF ? toF(c) : Math.round(c));
  const { Icon, cls } = iconFor(w.weather_code ?? 3);
  const sm = size === "sm";

  return (
    <div
      data-testid="home-weather-chip"
      title={`${w.city}${w.admin1 ? `, ${w.admin1}` : ""} — ${w.attribution || "Open-Meteo.com"}`}
      className={`flex items-center shrink-0 rounded-lg border bg-card/70 ${sm ? "gap-1.5 px-1.5 py-1" : "gap-2 rounded-xl px-2.5 py-1.5"}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Icon className={`${sm ? "h-4.5 w-4.5" : "h-6 w-6"} ${cls}`} style={sm ? { height: 18, width: 18 } : undefined} strokeWidth={2} />
      <div className="leading-tight">
        <div className={`${sm ? "text-[11px]" : "text-sm"} font-semibold tabular-nums`} data-testid="weather-current-temp">
          {fmt(w.temp_c)}°{useF ? "F" : "C"}
        </div>
        <div className={`${sm ? "text-[9px]" : "text-[10px]"} text-muted-foreground tabular-nums flex items-center gap-1`}>
          <span>{w.max_c != null ? fmt(w.max_c) : "–"}°/{w.min_c != null ? fmt(w.min_c) : "–"}°</span>
          {w.precip_prob != null && (
            <span className="flex items-center gap-0.5">
              <Droplets className={`${sm ? "h-2 w-2" : "h-2.5 w-2.5"} text-primary`} />
              {w.precip_prob}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
